import React, { useEffect, useState, useCallback } from "react";
import PracticesPage from "./pages/PracticesPage";
import PracticeDetailPage from "./pages/PracticeDetailPage";
import PracticeImportPage from "./pages/PracticeImportPage";
import OverviewPage from "./pages/OverviewPage";
import DirectoryPage from "./pages/DirectoryPage";
import DocumentArchivePage from "./pages/DocumentArchivePage";
import AdvancesPage from "./pages/AdvancesPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import QuotesPage from "./pages/QuotesPage";
import WellsPage from "./pages/WellsPage";
import SignPage from "./pages/SignPage";
import SettingsPage from "./pages/SettingsPage";
import { ReleaseDialog } from "./components/releases/ReleaseDialog";
import { api, apiBlob, apiUpload, downloadFile } from "./lib/api";
import { Module } from "./modules/RecordsModule";
import { AuditModule } from "./modules/AuditModule";
import { CoordinationModule } from "./modules/CoordinationModule";
import { newRowId, stripRowIds, withRowIds } from "./lib/rowKeys";
import { FinanceChart } from "./components/finance/FinanceChart";
import { ReceivablesPanel } from "./components/finance/ReceivablesPanel";
import { EmailLogPanel } from "./components/finance/EmailLogPanel";
import { BillingKpi } from "./components/billing/BillingKpi";
import { InvoiceDrafts } from "./components/billing/InvoiceDrafts";
import { ToInvoicePanel } from "./components/finance/ToInvoicePanel";
import { ClientFilter, clientOptions } from "./components/filters/ClientFilter";
import { LoginView } from "./components/LoginView";
import LegalPage from "./pages/LegalPage";
import PaymentResult from "./pages/PaymentResult";
import PricingPage from "./pages/PricingPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { NotificationBell } from "./components/NotificationBell";
import { BulkDeleteBar } from "./components/bulk/BulkDeleteBar";
import { useSelection } from "./components/bulk/useSelection";
import { GlobalSearch, shortcutLabel } from "./components/GlobalSearch";
import { AssistantWidget } from "./components/assistant/AssistantWidget";
import { NamingIntroDialog } from "./components/directory/NamingIntroDialog";
import { DocumentButton } from "./components/DocumentButton";
import { RequirementsEditor, defaultRequirements } from "./components/coordination/RequirementsEditor";
import { ClientSection } from "./components/coordination/ClientSection";
import { DocRequestBar, DocStateSelect } from "./components/coordination/DocRequest";
import { CompanyVerify } from "./components/coordination/CompanyVerify";
import { SiteVerify } from "./components/coordination/SiteVerify";
import DocRequestPage from "./pages/DocRequestPage";
import PosPage from "./pages/PosPage";
import { StatusBadge as RecordStatusBadge } from "./components/StatusBadge";
import { SortMenu } from "./components/sorting/SortMenu";
import { SortHeadCell } from "./components/sorting/SortableTh";
import { sortRows, useSortPreference } from "./lib/sorting";
import "@/App.css";
import "@/components/table/stickyGrids.css";
import { Bell, Building2, Calculator, CalendarClock, CheckCircle2, ChevronRight, Circle, ClipboardCheck, CreditCard, Download, FileText, HardHat, History, LayoutDashboard, LogOut, Mail, Menu, Pencil, Plus, Search, Settings2, ShieldAlert, Sparkles, Trash2, Upload, UserCheck, UserCog, UserPlus, Users, Waves, X, XCircle } from "lucide-react";

const baseNav = [
  ["dashboard", "Dashboard", LayoutDashboard], ["practices", "Pratiche", ClipboardCheck], ["clients", "Clienti", Users], ["sites", "Cantieri", Building2],
  ["coordination", "Coordinamento", HardHat],
  ["deadlines", "Attività e scadenze", CalendarClock], ["advances", "Acconti", CreditCard], ["documents", "Documenti", FileText],
  ["finance", "Economia", CreditCard], ["wells", "Pozzi artesiani", Waves],
  ["pos", "Convenzioni", ClipboardCheck],
  ["audit", "Storico", History],
];
const adminExtra = [];
const OPTIONAL_PAGES = ["coordination", "wells", "pos"];
const collections = ["clients","sites","deadlines","activities","practices","documents","quotes","invoices","economic","advances","wells"];
const emptyDraft = { name: "", site: "", event_date: "", expected_days: "", permit_issue_date: "", work_start_date: "", scia_submission_date: "", last_renewal_date: "", taxable: "", vat_rate: "", collected: "", assigned_to: "self", is_coordination: false };

export default function App() {
  const signToken = (window.location.pathname.match(/^\/firma\/([A-Za-z0-9_-]{8,})/) || [])[1];
  const legalPage = (window.location.pathname.match(/^\/(privacy|termini|supporto)\/?$/) || [])[1];
  const docRequestToken = (window.location.pathname.match(/^\/documenti\/([A-Za-z0-9_-]{8,})/) || [])[1];
  const publicPage = (window.location.pathname.match(/^\/(prezzi|reset-password)\/?$/) || [])[1];
  const paymentPage = (window.location.pathname.match(/^\/payment\/(success|cancel)\/?$/) || [])[1];
  const googleSession = (window.location.hash.match(/session_id=([^&]+)/) || [])[1];
  const [googleBusy, setGoogleBusy] = useState(!!googleSession);
  const [googleError, setGoogleError] = useState("");
  const [user, setUser] = useState(null);
  const [page, setPageState] = useState(() => window.location.hash.split("/")[1] || "dashboard");
  const [recordId, setRecordId] = useState(() => window.location.hash.split("/")[2] || "");
  const [createIntent, setCreateIntent] = useState("");
  const [invoiceIntent, setInvoiceIntent] = useState("");
  const [coordSite, setCoordSite] = useState("");
  const setPage = useCallback((value) => { window.location.hash = `/${value}`; setPageState(value); setRecordId(""); setCreateIntent(""); }, []);
  const openRecord = useCallback((collection, id) => { window.location.hash = `/${collection}/${id}`; setPageState(collection); setRecordId(id); }, []);
  useEffect(() => { const change = () => { setPageState(window.location.hash.split("/")[1] || "dashboard"); setRecordId(window.location.hash.split("/")[2] || ""); }; window.addEventListener("hashchange", change); return () => window.removeEventListener("hashchange", change); }, []);
  const [data, setData] = useState(() => Object.fromEntries(collections.map((c) => [c, []])));
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState({ clients: 0, sites: 0, deadlines: 0, documents: 0 });
  const [mobile, setMobile] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [settingsTab, setSettingsTab] = useState("studio");
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const handler = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); setSearchOpen(true); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
  const [releaseInfo, setReleaseInfo] = useState(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [releaseError, setReleaseError] = useState("");
  const [visited, setVisited] = useState(() => { try { return JSON.parse(localStorage.getItem("ingego-visited-sections") || "[]"); } catch (e) { return []; } });
  const markVisited = useCallback((id) => setVisited((old) => {
    if (old.includes(id)) return old;
    const next = [...old, id];
    localStorage.setItem("ingego-visited-sections", JSON.stringify(next));
    return next;
  }), []);
  const [scope, setScope] = useState("all"); // admin toggle: all | mine
  const [login, setLogin] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(emptyDraft);
  const [flash, setFlash] = useState("");
  const [modules, setModules] = useState({});
  const nav = (user?.role === "admin" ? [...baseNav, ...adminExtra] : baseNav)
    .filter(([key]) => !OPTIONAL_PAGES.includes(key) || modules[key] !== false);

  const loadAll = useCallback(async (currentUser) => {
    try {
      const s = await api(`/dashboard`);
      setStats(s);
    } catch (e) {}
    const qs = currentUser?.role === "admin" && scope === "mine" ? "?scope=mine" : "";
    for (const key of collections) {
      try {
        const rows = await api(`/${key}${qs}`);
        setData((old) => ({ ...old, [key]: rows }));
      } catch (e) {}
    }
    if (currentUser?.role === "admin") {
      try { setTeam(await api(`/users`)); } catch (e) {}
    }
  }, [scope]);

  useEffect(() => {
    if (googleSession) return;
    api(`/auth/me`).then((current) => {
      setUser(current);
      loadAll(current);
    }).catch(() => {});
  }, [loadAll, googleSession]);

  useEffect(() => {
    if (!googleSession) return;
    (async () => {
      try {
        const json = await api(`/auth/google/session`, { method: "POST", body: JSON.stringify({ session_id: googleSession }) });
        window.location.hash = "/dashboard";
        setPageState("dashboard");
        setUser(json.user);
        loadAll(json.user);
      } catch (e) {
        window.location.hash = "/dashboard";
        setGoogleError(e.message || "Accesso con Google non riuscito");
      } finally { setGoogleBusy(false); }
    })();
  }, [googleSession, loadAll]);

  useEffect(() => { if (user) loadAll(user); }, [scope, user, loadAll]);

  useEffect(() => {
    if (!user) return;
    api(`/releases/current`).then((info) => {
      setReleaseInfo(info);
      if (info.show_popup) setReleaseOpen(true);
    }).catch(() => {});
  }, [user]);

  async function confirmRelease() {
    const release = releaseInfo?.release;
    if (!release || releaseBusy) return false;
    setReleaseBusy(true); setReleaseError("");
    try {
      await api(`/releases/${release.id}/seen`, { method: "POST" });
      setReleaseInfo((old) => old ? { ...old, show_popup: false, release: { ...old.release, seen: true } } : old);
      setReleaseOpen(false);
      return true;
    } catch (e) { setReleaseError("Conferma non salvata. Riprova per non vedere nuovamente questo riepilogo."); return false; }
    finally { setReleaseBusy(false); }
  }

  async function submit(e) {
    e.preventDefault(); setError("");
    try {
      const json = await api(`/auth/login`, { method: "POST", body: JSON.stringify(login) });
      setUser(json.user); loadAll(json.user);
    } catch (e) { setError(e.message || "Impossibile accedere"); }
  }

  function collectionFor(p) {
    return p === "finance" ? "economic" : p === "deadlines" ? "activities" : p;
  }

  async function createRecord(e) {
    e.preventDefault();
    const collection = collectionFor(page);
    try {
      const payload = { ...draft, title: draft.name, status: "Da gestire" };
      if (user.role !== "admin") payload.assigned_to = "self";
      const item = await api(`/${collection}`, { method: "POST", body: JSON.stringify({ data: payload }) });
      setData((old) => ({ ...old, [collection]: [...(old[collection] || []), item] }));
      setShowCreate(false); setDraft(emptyDraft);
      setFlash("Elemento salvato correttamente"); setTimeout(() => setFlash(""), 2500);
    } catch (err) { setError(err.message); }
  }

  async function assign(collection, id, userId) {
    try {
      const updated = await api(`/${collection}/${id}/assign`, { method: "POST", body: JSON.stringify({ user_id: userId }) });
      setData((old) => ({ ...old, [collection]: (old[collection] || []).map((r) => r.id === id ? updated : r) }));
      setFlash(userId === user.id ? "Preso in carico" : "Assegnazione aggiornata"); setTimeout(() => setFlash(""), 2000);
    } catch (err) { setError(err.message); setTimeout(() => setError(""), 3500); }
  }

  async function removeRecord(collection, id) {
    const LABELS = { practices: "questa pratica", sites: "questo cantiere", clients: "questo cliente" };
    const row = (data[collection] || []).find((r) => r.id === id);
    const name = row?.name || row?.title || "";
    const message = LABELS[collection]
      ? `Eliminare definitivamente ${LABELS[collection]}${name ? ` "${name}"` : ""}?\n\nL'operazione non è reversibile.`
      : "Eliminare definitivamente questo elemento? L'operazione non è reversibile.";
    if (!window.confirm(message)) return;
    try {
      await api(`/${collection}/${id}`, { method: "DELETE" });
      setData((old) => ({ ...old, [collection]: (old[collection] || []).filter((r) => r.id !== id) }));
    } catch (err) { setError(err.message); setTimeout(() => setError(""), 3500); }
  }

  useEffect(() => {
    if (!user) return;
    api("/settings/modules").then(setModules).catch(() => setModules({}));
  }, [user]);

  const [practiceTab, setPracticeTab] = useState("");
  const openTarget = (target) => {
    if (!target?.page) return;
    setPage(target.page);
    setPracticeTab(target.page === "practices" ? target.tab || "" : "");
    if (target.record_id && ["practices", "clients", "sites"].includes(target.page)) openRecord(target.page, target.record_id);
  };

  const navigate = (target) => {
    const [next, tab] = String(target).split(":");
    if (next === "practices" && tab === "to-invoice") { setInvoiceIntent("yes"); setRecordId(""); setPage("practices"); return; }
    if (tab) setSettingsTab(tab);
    setPage(next);
  };

  if (signToken) return <SignPage token={signToken} />;
  if (docRequestToken) return <DocRequestPage token={docRequestToken} />;
  if (legalPage) return <LegalPage page={legalPage} />;
  if (publicPage === "prezzi") return <PricingPage />;
  if (publicPage === "reset-password") return <ResetPasswordPage />;
  if (paymentPage) return <PaymentResult outcome={paymentPage === "cancel" ? "cancel" : "success"} />;
  if (googleBusy) return <div className="login-working" data-testid="google-callback">
    <img src="/brand/ingego-logo.png" alt="ingego" />
    <span>Accesso con Google in corso…</span>
  </div>;
  if (!user) return <LoginView login={login} setLogin={setLogin} submit={submit} error={error} googleError={googleError} />;

  const active = [...nav, ["estimates", "Preventivazione", Calculator], ["settings", "Impostazioni", Settings2], ["ai", "INGEGO AI", Sparkles]].find((n) => n[0] === page) || nav[0];
  const currentRelease = releaseInfo?.release;
  const freshRelease = currentRelease && currentRelease.published_at
    && (Date.now() - new Date(currentRelease.published_at).getTime()) < 14 * 86400000;
  const newTargets = freshRelease
    ? (currentRelease.items || []).filter((item) => ["nuovo", "ai"].includes(item.category) && item.target && !visited.includes(item.target)).map((item) => item.target)
    : [];
  const rows = (page === "finance" ? [...data.quotes, ...data.invoices, ...data.economic]
    : page === "deadlines" ? [...data.deadlines.map(d => ({ ...d, _source: "deadlines" })), ...data.activities.map(a => ({ ...a, _source: "activities" }))]
    : data[page] || []);

  return (
    <div className="app-shell">
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <div className="side-brand"><img src="/brand/ingego-logo-light.png" alt="ingego" className="brand-logo" /><button className="mobile-close" onClick={() => setMobile(false)} data-testid="close-menu-button"><X size={18} /></button></div>
        <div className="side-section">WORKSPACE</div>
        <nav>{nav.map(([id, label, Icon]) => <button key={id} data-testid={`nav-${id}-button`} className={page === id ? "nav-item active" : "nav-item"} onClick={() => { setPage(id); markVisited(id); setMobile(false); }}><Icon size={18} /><span>{label}</span>{newTargets.includes(id) && <span className="release-nav-badge" data-testid={`nav-new-${id}`}>NUOVO</span>}</button>)}</nav>
        <button className={page === "ai" ? "nav-ai-brand active" : "nav-ai-brand"} data-testid="nav-ai-button" onClick={() => { setPage("ai"); markVisited("ai"); setMobile(false); }}>
          <span className="nav-ai-mark"><Sparkles size={16} /></span>
          <span className="nav-ai-text"><strong>INGEGO AI</strong><small>Il tuo studio, più veloce</small></span>
          <ChevronRight size={15} />
        </button>
        <div className="side-bottom">
          <button className={page === "estimates" ? "nav-item nav-estimates active" : "nav-item nav-estimates"} data-testid="nav-estimates-button" onClick={() => { setPage("estimates"); setMobile(false); }}><Calculator size={18} /><span>Preventivazione</span></button>
          <button className={page === "settings" ? "nav-item active" : "nav-item"} data-testid="settings-button" onClick={() => { setPage("settings"); setMobile(false); }}><Settings2 size={18} />Impostazioni</button>
          <div className="profile">
            <span className="avatar">{user.name.slice(0,2).toUpperCase()}</span>
            <div><strong>{user.name}</strong><small>{user.role === "admin" ? "Amministratore" : "Consulente"}</small></div>
            <button data-testid="logout-button" onClick={async () => { try { await api("/auth/logout", { method: "POST" }); } catch (e) {} localStorage.clear(); setUser(null); }}><LogOut size={16} /></button>
          </div>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <button className="menu-button" onClick={() => setMobile(true)} data-testid="open-menu-button"><Menu /></button>
          <div className="crumb">Workspace <ChevronRight size={14} /><strong>{active[1]}</strong></div>
          <div className="top-actions">
            <button className="search-trigger" data-testid="global-search-button" onClick={() => setSearchOpen(true)}><Search size={16} /><span>Cerca in tutto il gestionale</span><kbd>{shortcutLabel()}</kbd></button>
            <NotificationBell onOpenTarget={openTarget} />
            <div className="top-avatar">{user.name.slice(0,2).toUpperCase()}</div>
          </div>
        </header>
        <section className="content">
          <div className="page-intro" style={recordId || ["dashboard", "settings", "releases"].includes(page) ? {display:"none"} : undefined}>
            <div>
              <p className="eyebrow">{page === "dashboard" ? "GESTIONE STUDIO" : "GESTIONE OPERATIVA"}</p>
              <h1 data-testid="page-title">{page === "dashboard" ? <>Benvenuto, <i>{user.name.split(" ")[0]}.</i></> : active[1]}</h1>
              <p className="muted">{page === "dashboard" ? (user.role === "admin" ? "Panoramica completa dello studio." : "Ecco i tuoi lavori attivi e quelli da prendere in carico.") : `Tieni sotto controllo ${active[1].toLowerCase()} e attività collegate.`}</p>
            </div>
            <div style={{display:"flex", gap:10, alignItems:"center", flexWrap:"wrap"}}>
              {user.role === "admin" && !["dashboard", "ai", "audit", "documents", "coordination", "releases"].includes(page) && (
                <div className="toggle">
                  <button data-testid="scope-all" onClick={() => setScope("all")} className={scope==="all" ? "on" : ""}>Tutti</button>
                  <button data-testid="scope-mine" onClick={() => setScope("mine")} className={scope==="mine" ? "on" : ""}>Miei</button>
                </div>
              )}
              {!["practices", "clients", "sites", "advances", "team", "dashboard", "ai", "audit", "documents", "coordination", "deadlines", "estimates", "settings", "wells", "pos", "releases"].includes(page) && <button className="primary" data-testid="add-record-button" onClick={() => setShowCreate(true)}><Plus size={16} />Nuovo elemento</button>}
            </div>
          </div>
          {flash && <div className="flash" data-testid="flash-message">{flash}</div>}
          {error && <div className="error" data-testid="app-error" style={{marginBottom:20}}>{error}</div>}
          {page === "dashboard" ? <OverviewPage data={data} team={team} user={user} go={navigate} openRecord={openRecord} onCreate={(p) => { setPage(p); setCreateIntent(p); }} />
            : page === "ai" ? <PracticeImportPage practices={data.practices} clients={data.clients} sites={data.sites} onOpen={id => { openRecord("practices", id); loadAll(user); }} onChanged={() => loadAll(user)} />
            : page === "practices" ? recordId ? <PracticeDetailPage key={`${recordId}-${practiceTab}`} initialTab={practiceTab || "overview"} practiceId={recordId} clients={data.clients} sites={data.sites} onNavigate={openRecord} onBack={() => { setPage("practices"); loadAll(user); }} onChanged={() => loadAll(user)} /> : <PracticesPage initialCreate={createIntent === "practices"} initialInvoice={invoiceIntent} onClearInvoice={() => setInvoiceIntent("")} rows={rows} clients={data.clients} sites={data.sites} team={team} user={user} onOpen={id => { openRecord("practices", id); loadAll(user); }} onImport={() => setPage("ai")} onAssign={assign} onDelete={removeRecord} onBulkDeleted={() => loadAll(user)} />
            : ["clients", "sites"].includes(page) ? <DirectoryPage initialCreate={createIntent === page} key={`${page}-${recordId}`} collection={page} recordId={recordId} rows={rows} clients={data.clients} practices={data.practices} onOpen={openRecord} onBack={() => setPage(page)} onChanged={() => loadAll(user)} onOpenCoordination={siteId => { setCoordSite(siteId); setPage("coordination"); }} />
            : page === "advances" ? <AdvancesPage initialOpen={createIntent === "advances"} practices={data.practices} wells={data.wells} advances={data.advances} onChanged={() => loadAll(user)} onOpen={id => openRecord("practices", id)} />
            : page === "audit" ? <AuditModule />
            : page === "documents" ? <DocumentArchivePage practices={data.practices} clients={data.clients} user={user} onOpen={openRecord} onChanged={() => loadAll(user)} />
            : page === "deadlines" ? <ActivitiesPage rows={rows} practices={data.practices} clients={data.clients} team={team} user={user} initialCreate={createIntent === "deadlines"} onChanged={() => loadAll(user)} onOpenPractice={id => { setPage("practices"); openRecord("practices", id); }} />
            : page === "estimates" ? <QuotesPage clients={data.clients} user={user} initialCreate={createIntent === "estimates"} onChanged={() => loadAll(user)} onOpenPractice={id => { setPage("practices"); openRecord("practices", id); }} />
            : page === "coordination" ? <CoordinationModule user={user} initialSite={coordSite} onClearInitial={() => setCoordSite("")} />
            : page === "wells" ? <WellsPage rows={rows} clients={data.clients} practices={data.practices} user={user} onChanged={() => loadAll(user)} onOpenPractice={id => { setPage("practices"); openRecord("practices", id); }} />
            : page === "pos" ? <PosPage clients={data.clients} user={user} />
            : page === "settings" ? <SettingsPage user={user} initialTab={settingsTab} onUser={setUser} go={navigate} />
            : page === "finance" ? <><BillingKpi /><FinanceChart /><div className="practice-workspace"><ToInvoicePanel onOpenPractice={id => { setPage("practices"); openRecord("practices", id); }} onChanged={() => loadAll(user)} /></div><div className="practice-workspace"><InvoiceDrafts onChanged={() => loadAll(user)} /></div><ReceivablesPanel /><EmailLogPanel /><Module page={page} rows={rows} team={team} user={user} onAssign={assign} onDelete={removeRecord} collectionFor={collectionFor} clients={data.clients} /></>
            : <Module page={page} rows={rows} team={team} user={user} onAssign={assign} onDelete={removeRecord} collectionFor={collectionFor} />}
        </section>
        <footer className="app-foot" data-testid="app-footer">
          <img src="/brand/ingego-mark.png" alt="ingego" />
          <span>ingego · Engineering, organized.</span>
        </footer>
      </main>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} onOpenTarget={openTarget} />
      <AssistantWidget user={user} />
      <NamingIntroDialog />
      {releaseOpen && <ReleaseDialog release={currentRelease} limit={releaseInfo?.popup_limit || 6} busy={releaseBusy} error={releaseError} onClose={confirmRelease}
        onAll={async () => { if (await confirmRelease()) navigate("settings:info"); }}
        go={async target => { if (await confirmRelease()) { markVisited(target); navigate(target); } }} />}
      {showCreate && (
        <div className="modal-backdrop">
          <form className="create-modal" onSubmit={createRecord}>
            <button type="button" className="modal-close" data-testid="close-create-modal-button" onClick={() => setShowCreate(false)}><X /></button>
            <p className="eyebrow">NUOVA REGISTRAZIONE</p>
            <h2>Aggiungi a {active[1]}</h2>
            <label>Nome o titolo<input data-testid="create-name-input" required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            <label>Cantiere / riferimento<input data-testid="create-site-input" value={draft.site} onChange={(e) => setDraft({ ...draft, site: e.target.value })} /></label>
            {page === "deadlines" && <>
              <label>Pratica<select data-testid="create-activity-practice" value={draft.practice_id || ""} onChange={e => setDraft({...draft, practice_id:e.target.value})}><option value="">Nessuna pratica</option>{data.practices.map(p => <option key={p.id} value={p.id}>{p.name || "Pratica da completare"}</option>)}</select></label>
              <label>Data evento<input data-testid="create-event-date-input" type="date" value={draft.event_date} onChange={(e) => setDraft({ ...draft, event_date: e.target.value })} /></label>
              <label>Giorni previsti<input data-testid="create-expected-days-input" type="number" value={draft.expected_days} onChange={(e) => setDraft({ ...draft, expected_days: e.target.value })} /></label>
            </>}
            {page === "practices" && <>
              <label>Rilascio PdC<input data-testid="create-permit-issue-date-input" type="date" value={draft.permit_issue_date} onChange={(e) => setDraft({ ...draft, permit_issue_date: e.target.value })} /></label>
              <label>Inizio lavori<input data-testid="create-work-start-date-input" type="date" value={draft.work_start_date} onChange={(e) => setDraft({ ...draft, work_start_date: e.target.value })} /></label>
              <label>Presentazione SCIA<input data-testid="create-scia-submission-date-input" type="date" value={draft.scia_submission_date} onChange={(e) => setDraft({ ...draft, scia_submission_date: e.target.value })} /></label>
            </>}
            {page === "wells" && <label>Ultimo rinnovo<input data-testid="create-last-renewal-date-input" type="date" value={draft.last_renewal_date} onChange={(e) => setDraft({ ...draft, last_renewal_date: e.target.value })} /></label>}
            {page === "sites" && <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"12px 14px",background:"var(--wine-soft)",borderRadius:10,margin:"14px 0",textTransform:"none",letterSpacing:0}}>
              <input data-testid="create-is-coordination-input" type="checkbox" checked={!!draft.is_coordination} onChange={(e) => setDraft({...draft, is_coordination: e.target.checked})} style={{width:18,height:18,accentColor:"var(--wine)",margin:0}} />
              <span style={{fontSize:13,fontWeight:600,color:"var(--wine)"}}>Cantiere in coordinamento (CSE/CSP)</span>
            </label>}
            {page === "finance" && <>
              <label>Imponibile<input data-testid="create-taxable-input" type="number" value={draft.taxable} onChange={(e) => setDraft({ ...draft, taxable: e.target.value })} /></label>
              <label>IVA %<input data-testid="create-vat-rate-input" type="number" value={draft.vat_rate} onChange={(e) => setDraft({ ...draft, vat_rate: e.target.value })} /></label>
              <label>Incassato<input data-testid="create-collected-input" type="number" value={draft.collected} onChange={(e) => setDraft({ ...draft, collected: e.target.value })} /></label>
            </>}
            {user.role === "admin" && <label>Assegna a<select data-testid="create-assign-select" value={draft.assigned_to} onChange={(e) => setDraft({ ...draft, assigned_to: e.target.value })}>
              <option value="self">A me stesso</option>
              <option value="">Non assegnato (disponibile)</option>
              {team.filter((t) => t.id !== user.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></label>}
            <button className="primary wide" data-testid="create-submit-button">Salva elemento <ChevronRight size={17} /></button>
          </form>
        </div>
      )}
    </div>
  );
}

